import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useState } from "react";
import { BrowserRouter, Link, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { Layout } from "@/components/Layout";
import { EmptyState, Spinner } from "@/components/ui";
import { ApiError } from "@/lib/api";
import { AuthProvider, useAuth } from "@/lib/auth";
import { LiveProvider } from "@/lib/live";
import type { Role } from "@/lib/types";
import { Fleet } from "@/pages/Fleet";
import { Login } from "@/pages/Login";

// Pages beyond the first screen are loaded when they are opened.
const Device = lazy(() => import("@/pages/Device").then((m) => ({ default: m.Device })));
const Broadcast = lazy(() => import("@/pages/Broadcast").then((m) => ({ default: m.Broadcast })));
const Alerts = lazy(() => import("@/pages/Alerts").then((m) => ({ default: m.Alerts })));
const Admin = lazy(() => import("@/pages/Admin").then((m) => ({ default: m.Admin })));
const MapPage = lazy(() => import("@/pages/Map").then((m) => ({ default: m.MapPage })));

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        refetchOnWindowFocus: true,
        retry: (failures, error) => {
          // Asking again doesn't change "not found" or "not allowed".
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
          return failures < 2;
        },
      },
    },
  });
}

/** Lets signed-in staff through and sends everyone else to the sign-in page. */
function SignedIn() {
  const { session } = useAuth();
  const location = useLocation();
  if (session.status === "loading") {
    return (
      <div className="grid min-h-full place-items-center">
        <Spinner label="Signing in" />
      </div>
    );
  }
  if (session.status === "signed-out") {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return (
    <LiveProvider>
      <Outlet />
    </LiveProvider>
  );
}

function Requires({ role }: { role: Role }) {
  const { can } = useAuth();
  if (!can(role)) {
    return (
      <div className="card">
        <EmptyState title="This page needs more rights than your account has">
          Ask an administrator if you need access.
        </EmptyState>
      </div>
    );
  }
  return <Outlet />;
}

function NotFound() {
  return (
    <div className="card">
      <EmptyState
        title="This page doesn't exist"
        action={
          <Link to="/" className="font-medium text-link hover:underline">
            Go to the fleet
          </Link>
        }
      />
    </div>
  );
}

export function App() {
  const [queryClient] = useState(createQueryClient);
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<Spinner />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route element={<SignedIn />}>
                <Route element={<Layout />}>
                  <Route index element={<Fleet />} />
                  <Route path="map" element={<MapPage />} />
                  <Route path="devices/:id" element={<Device />} />
                  <Route path="commands" element={<Broadcast />} />
                  <Route path="alerts" element={<Alerts />} />
                  <Route element={<Requires role="operator" />}>
                    <Route path="admin" element={<Admin />} />
                  </Route>
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Route>
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
      <Toaster
        theme="dark"
        position="bottom-right"
        closeButton
        toastOptions={{
          style: {
            background: "var(--color-raised)",
            border: "1px solid rgb(255 255 255 / 0.1)",
            color: "var(--color-ink)",
          },
        }}
      />
    </QueryClientProvider>
  );
}
